///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { AccessorySections } from '../../entities/product-accessory/AccessorySections.enum';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AccessoryCompatibilityTypeEnum, ProductAccessoryDTO, ProductAccessoryTypeEnum } from '../../generated/shared-types.model';

@Injectable()
export class ProductAccessoryService {
    private readonly resourceUrl = 'api/product-accessories';

    constructor(private readonly http: HttpClient) {}

    create(productAccessory: ProductAccessoryDTO): Observable<ProductAccessoryDTO> {
        return this.http
            .post<ProductAccessoryDTO>(this.resourceUrl, productAccessory)
            .pipe(catchError(() => of({} as ProductAccessoryDTO)));
    }

    update(productAccessory: ProductAccessoryDTO): Observable<ProductAccessoryDTO> {
        return this.http.put<ProductAccessoryDTO>(this.resourceUrl, productAccessory).pipe(catchError(() => of({} as ProductAccessoryDTO)));
    }

    find(id: number): Observable<ProductAccessoryDTO> {
        return this.http.get<ProductAccessoryDTO>(`${this.resourceUrl}/${id}`).pipe(catchError(() => of(undefined)));
    }

    queryByType(type: AccessoryCompatibilityTypeEnum): Observable<ProductAccessoryDTO[]> {
        return this.http.get<ProductAccessoryDTO[]>(`${this.resourceUrl}/type/${type}`).pipe(catchError(() => of([])));
    }

    queryByTypeWithCommon(type: AccessoryCompatibilityTypeEnum): Observable<ProductAccessoryDTO[]> {
        return this.http.get<ProductAccessoryDTO[]>(`${this.resourceUrl}/type/${type}/with-common`).pipe(catchError(() => of([])));
    }

    queryForProject(projectId: number): Observable<ProductAccessoryDTO[]> {
        return this.http.get<ProductAccessoryDTO[]>(`${this.resourceUrl}/project/${projectId}`).pipe(catchError(() => of([])));
    }

    delete(id: number): Observable<number> {
        return this.http.delete<number>(`${this.resourceUrl}/${id}`);
    }

    sortByAccessoryType(productAccessories: ProductAccessoryDTO[]): ProductAccessoryDTO[][] {
        const productAccessoriesByType = [];

        productAccessories.forEach((accessory) => {
            let accessorySection;
            switch (accessory.accessoryType) {
                case ProductAccessoryTypeEnum.SOFTWARE_UPGRADE:
                case ProductAccessoryTypeEnum.OPTIONS:
                case ProductAccessoryTypeEnum.OPT_SYNC:
                case ProductAccessoryTypeEnum.OPT_L2:
                case ProductAccessoryTypeEnum.OPT_AES:
                case ProductAccessoryTypeEnum.OPT_EXTENDMM:
                case ProductAccessoryTypeEnum.OPT_PSE:
                case ProductAccessoryTypeEnum.SECTOR_LICENSE:
                    accessorySection = AccessorySections.SW_LICENCES;
                    break;
                case ProductAccessoryTypeEnum.ANTENNA:
                    accessorySection = AccessorySections.ANTENNA;
                    break;
                case ProductAccessoryTypeEnum.MK:
                    accessorySection = AccessorySections.MK;
                    break;
                case ProductAccessoryTypeEnum.POWER_INJECTORS:
                    accessorySection = AccessorySections.POWER_INJECTORS;
                    break;
                case ProductAccessoryTypeEnum.SRG:
                    accessorySection = AccessorySections.SRG;
                    break;
                case ProductAccessoryTypeEnum.SFP:
                    accessorySection = AccessorySections.SFP;
                    break;
                case ProductAccessoryTypeEnum.MISC:
                    accessorySection = AccessorySections.MISCELLANEOUS;
                    break;
                case ProductAccessoryTypeEnum.COMPOSITE:
                    accessorySection = AccessorySections.COMPOSITE;
                    break;
            }
            if (!productAccessoriesByType[accessorySection]) {
                productAccessoriesByType[accessorySection] = [];
            }
            productAccessoriesByType[accessorySection].push(accessory);
        });

        return productAccessoriesByType;
    }
}
